import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Trash2, Plus, Minus, ArrowLeft, Wallet, MapPin, AlertTriangle, CalendarIcon, Clock } from 'lucide-react';
import PublicLayout from '../../components/shared/PublicLayout';
import { useCart } from '../../contexts/CartContext';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiBase } from '../../lib/api';
import { getPublicImageUrl } from '../../lib/media';
import { format } from 'date-fns';

const API = getApiBase();

const isOfferRewardItem = (item) => !!item?.offer_reward_meta;

const parisParts = (date = new Date()) => Object.fromEntries(
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type, part.value])
);

const timeToMinutes = (value) => {
  const [hours, minutes] = String(value || '').split(':').map(Number);
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null;
};

const timeFromMinutes = (value) => `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;

function pickupSlots(settings, orderType) {
  const prefix = orderType === 'delivery' ? 'delivery' : 'takeaway';
  if (!settings || settings[`${prefix}_enabled`] === false) return [];
  const start = timeToMinutes(settings[`${prefix}_hours_start`]);
  const end = timeToMinutes(settings[`${prefix}_hours_end`]);
  const breakStart = timeToMinutes(settings[`${prefix}_break_start`]);
  const breakEnd = timeToMinutes(settings[`${prefix}_break_end`]);
  if (start === null || end === null || start > end) return [];

  const now = new Date();
  const minimum = new Date(now.getTime() + (orderType === 'delivery' ? 30 : 10) * 60 * 1000);
  minimum.setMinutes(minimum.getMinutes() + ((5 - minimum.getMinutes() % 5) % 5), 0, 0);
  const minimumParts = parisParts(minimum);
  const minimumDate = `${minimumParts.year}-${minimumParts.month}-${minimumParts.day}`;
  const minimumTime = `${minimumParts.hour}:${minimumParts.minute}`;
  const todayParts = parisParts(now);
  const today = new Date(Date.UTC(Number(todayParts.year), Number(todayParts.month) - 1, Number(todayParts.day)));
  const slots = [];

  for (let offset = 0; offset < 7; offset += 1) {
    const day = new Date(today);
    day.setUTCDate(day.getUTCDate() + offset);
    const date = day.toISOString().slice(0, 10);
    for (let minute = start; minute <= end; minute += 5) {
      if (breakStart !== null && breakEnd !== null && minute >= breakStart && minute < breakEnd) continue;
      const time = timeFromMinutes(minute);
      if (date < minimumDate || (date === minimumDate && time < minimumTime)) continue;
      const dayLabel = offset === 0 ? "Aujourd'hui" : offset === 1 ? 'Demain' : day.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
      slots.push({ date, time, label: `${dayLabel} - ${time}` });
    }
  }
  return slots;
}

export default function CartPage() {
  const { items, orderType, setOrderType, total, removeItem, updateQuantity, clearCart, tableInfo, addItem } = useCart();
  const [customer, setCustomer] = useState({ first_name: '', name: '', email: '', phone: '', address: '' });
  const [pickupTime, setPickupTime] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupPreference, setPickupPreference] = useState('scheduled');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [distanceCheck, setDistanceCheck] = useState(null);
  const [checkingDistance, setCheckingDistance] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const navigate = useNavigate();
  const configuredDeliveryFee = settings?.delivery_fee || 3.50;
  const deliveryFee = orderType === 'delivery' ? configuredDeliveryFee : 0;
  const cardFeePercent = Number(settings?.payment_card_fee_percent || 1.75);
  const cardProcessingFee = paymentMethod === 'card'
    ? Number((((total + (orderType === 'delivery' ? deliveryFee : 0)) * cardFeePercent) / 100).toFixed(2))
    : 0;
  const grandTotal = total + (orderType === 'delivery' ? deliveryFee : 0) + cardProcessingFee;
  const availablePickupSlots = useMemo(
    () => (!tableInfo ? pickupSlots(settings, orderType) : []),
    [settings, orderType, tableInfo]
  );
  const pickupDates = [...new Set(availablePickupSlots.map(slot => slot.date))];
  const selectedPickupDate = pickupDate ? new Date(`${pickupDate}T12:00:00`) : null;
  const selectedDateSlots = availablePickupSlots.filter(slot => slot.date === pickupDate);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/settings`),
      axios.get(`${API}/api/dishes?available_only=true`)
    ]).then(([settingsResponse, dishesResponse]) => {
      setSettings(settingsResponse.data);
      setDishes(dishesResponse.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!settings || tableInfo) return;
    const availableModes = [];
    if (settings.delivery_enabled !== false) availableModes.push('delivery');
    if (settings.takeaway_enabled !== false) availableModes.push('takeaway');
    if (!availableModes.length) return;
    if (!availableModes.includes(orderType)) {
      setOrderType(availableModes[0]);
    }
  }, [settings, tableInfo, orderType, setOrderType]);

  useEffect(() => {
    if (tableInfo || pickupPreference !== 'scheduled') return;
    const selected = availablePickupSlots.find(slot => slot.date === pickupDate && slot.time === pickupTime);
    if (!selected) {
      const firstSlot = availablePickupSlots[0];
      setPickupDate(firstSlot?.date || '');
      setPickupTime(firstSlot?.time || '');
    }
  }, [orderType, pickupPreference, tableInfo, settings, pickupDate, pickupTime, availablePickupSlots]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const checkDistance = async (address) => {
    if (!address || orderType !== 'delivery') return;
    setCheckingDistance(true);
    try {
      const geo = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`);
      const results = await geo.json();
      if (results.length === 0) { setDistanceCheck({ error: 'Adresse non trouvee' }); return; }
      const { lat, lon } = results[0];
      const { data } = await axios.post(`${API}/api/settings/check-distance`, { lat: parseFloat(lat), lng: parseFloat(lon) });
      setDistanceCheck(data);
      if (!data.within_range) toast.error(`Vous etes a ${data.distance_km}km. Livraison max: ${data.max_km}km`);
    } catch { setDistanceCheck(null); }
    finally { setCheckingDistance(false); }
  };

  const paymentMethodLabels = {
    cash: 'Argent liquide',
    card: 'Carte bleue en ligne',
    ticket_restaurant: 'Ticket restaurant',
    card_on_receipt: 'Carte bancaire a la reception',
  };
  const paymentMethodSettingKeys = {
    cash: 'payment_method_cash_enabled',
    card: 'payment_method_card_enabled',
    ticket_restaurant: 'payment_method_ticket_restaurant_enabled',
    card_on_receipt: 'payment_method_other_enabled',
  };
  const availablePaymentMethods = Object.entries(paymentMethodLabels).filter(([key]) => settings?.[paymentMethodSettingKeys[key]] !== false);
  const availableOrderModes = [
    settings?.takeaway_enabled !== false ? { key: 'takeaway', label: 'A emporter' } : null,
    settings?.delivery_enabled !== false ? { key: 'delivery', label: 'Livraison' } : null,
  ].filter(Boolean);
  const paidItems = items.filter(item => !isOfferRewardItem(item));
  const activeOffers = (settings?.offers || []).filter(offer => offer.active !== false);

  const unlockedOffers = activeOffers.map((offer) => {
    const triggerIds = new Set(offer.trigger_dish_ids || []);
    const rewardIds = new Set(offer.reward_dish_ids || []);
    const triggerCount = paidItems.reduce((sum, item) => (
      triggerIds.has(item.dish_id) ? sum + Number(item.quantity || 0) : sum
    ), 0);
    const claimableSets = Math.floor(triggerCount / Math.max(1, Number(offer.trigger_quantity || 1)));
    const maxRewardCount = claimableSets * Math.max(1, Number(offer.reward_quantity || 1));
    const claimedRewardCount = items.reduce((sum, item) => (
      item.offer_reward_meta?.offer_id === offer.id ? sum + Number(item.quantity || 0) : sum
    ), 0);
    const remainingRewardCount = Math.max(0, maxRewardCount - claimedRewardCount);
    const rewardOptions = dishes.filter(dish => rewardIds.has(dish.id));
    return {
      ...offer,
      claimableSets,
      maxRewardCount,
      claimedRewardCount,
      remainingRewardCount,
      rewardOptions,
    };
  }).filter(offer => offer.claimableSets > 0);

  const fulfillmentMessage = tableInfo
    ? 'Le paiement sera effectue sur place apres le service.'
    : orderType === 'delivery'
      ? 'Le paiement sera effectue a la livraison.'
      : 'Le paiement sera effectue a la recuperation de la commande.';
  const paymentTimingMessage = paymentMethod === 'card'
    ? 'Le paiement par carte sera effectue immediatement sur la page SumUp securisee.'
    : fulfillmentMessage;

  useEffect(() => {
    if (!availablePaymentMethods.length) return;
    if (!availablePaymentMethods.some(([key]) => key === paymentMethod)) {
      setPaymentMethod(availablePaymentMethods[0][0]);
    }
  }, [paymentMethod, availablePaymentMethods]);

  const handleOrder = async () => {
    if (!customer.first_name || !customer.name || !customer.phone) {
      toast.error('Veuillez renseigner votre prenom, nom et telephone');
      return;
    }
    if (orderType === 'delivery' && !customer.address) {
      toast.error('Veuillez renseigner votre adresse de livraison');
      return;
    }
    if (orderType === 'delivery' && distanceCheck && !distanceCheck.within_range) {
      toast.error('Votre adresse est hors de notre zone de livraison');
      return;
    }
    if (settings?.exceptional_closure_enabled) {
      toast.error(settings.exceptional_closure_message || 'Le restaurant est temporairement ferme');
      return;
    }
    if (!paymentMethod) {
      toast.error('Veuillez choisir un mode de paiement');
      return;
    }
    if (!tableInfo && pickupPreference === 'scheduled' && (!pickupDate || !pickupTime)) {
      toast.error('Aucun creneau de retrait n est disponible pour le moment');
      return;
    }
    setLoading(true);
    try {
      const orderData = {
        type: tableInfo ? 'dine_in' : orderType,
        items: items.map(i => ({
          dish_id: i.dish_id,
          name: i.name,
          quantity: i.quantity,
          price: i.price,
          supplements: i.supplements,
          formula_selections: i.formula_selections || [],
          vat_rate: i.vat_rate,
        })),
        customer,
        table_id: tableInfo?.id || '',
        table_number: tableInfo?.number || 0,
        pickup_time: pickupPreference === 'scheduled' ? pickupTime : '',
        pickup_date: pickupPreference === 'scheduled' ? pickupDate : '',
        pickup_preference: pickupPreference,
        delivery_fee: orderType === 'delivery' ? deliveryFee : 0,
        payment_method: paymentMethod,
        payment_method_note: '',
      };
      const { data: order } = await axios.post(`${API}/api/orders`, orderData);

      clearCart();
      if (paymentMethod === 'card') {
        const { data: checkout } = await axios.post(`${API}/api/payments/create-session`, {
          order_id: order.id,
          origin_url: window.location.origin,
        });
        window.location.assign(checkout.url);
        return;
      }
      toast.success('Commande envoyee !');
      navigate(`/checkout/success?order_id=${order.id}&payment_method=${paymentMethod}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setLoading(false);
    }
  };

  const addRewardItem = (offer, dish) => {
    if (offer.remainingRewardCount <= 0) {
      toast.error("Offre deja utilisee");
      return;
    }
    addItem(dish, 1, [], orderType, {
      displayName: `${dish.name} - Offert`,
      priceOverride: 0,
      vatRate: dish.vat_rate,
      offerRewardMeta: {
        offer_id: offer.id,
        offer_title: offer.title,
      },
    });
    toast.success(`${dish.name} ajoute en article offert`);
  };

  if (items.length === 0) {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-24 text-center">
          <h1 className="font-heading text-3xl font-bold text-[#2A2A28] mb-4">Votre panier est vide</h1>
          <p className="text-[#7A7A75] mb-8">Parcourez notre carte pour ajouter des plats.</p>
          <Button onClick={() => navigate('/commander')} className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="go-to-menu-btn">
            Voir la carte
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#7A7A75] hover:text-[#5B6B3F] mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#2A2A28] tracking-tight mb-8">Votre Panier</h1>
        {settings?.exceptional_closure_enabled && (
          <div className="mb-6 rounded-2xl border border-[#8C3A3A]/20 bg-[#8C3A3A]/8 px-4 py-3 text-sm text-[#8C3A3A]">
            {settings.exceptional_closure_message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {!tableInfo && (
              <div className="flex gap-2 mb-4" data-testid="cart-order-type">
                {availableOrderModes.map(t => (
                  <Button key={t.key} variant={orderType === t.key ? 'default' : 'outline'}
                    className={`rounded-full ${orderType === t.key ? 'bg-[#5B6B3F] text-white' : 'border-[#EBE8E0] text-[#7A7A75]'}`}
                    onClick={() => setOrderType(t.key)} data-testid={`cart-type-${t.key}`}>
                    {t.label}
                  </Button>
                ))}
              </div>
            )}

            {items.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#EBE8E0] p-4 flex items-center gap-4" data-testid={`cart-item-${i}`}>
                {item.image && <img src={getPublicImageUrl(item.image)} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#2A2A28] truncate">{item.name}</h3>
                  <p className="text-sm text-[#7A7A75]">{item.price.toFixed(2)} EUR</p>
                  {isOfferRewardItem(item) && (
                    <p className="text-xs text-[#D4AF37]">Article offert via {item.offer_reward_meta?.offer_title || 'une offre'}</p>
                  )}
                  {(item.formula_selections || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {item.formula_selections.map((group, index) => (
                        <p key={index} className="text-xs text-[#7A7A75]">
                          <span className="font-medium text-[#2A2A28]">{group.group_name}:</span> {group.items?.map(option => option.name).join(', ')}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                {!isOfferRewardItem(item) ? (
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(i, item.quantity - 1)} className="w-8 h-8 rounded-full border border-[#EBE8E0] flex items-center justify-center hover:bg-[#FAF8F5]" data-testid={`decrease-${i}`}>
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <button onClick={() => updateQuantity(i, item.quantity + 1)} className="w-8 h-8 rounded-full border border-[#EBE8E0] flex items-center justify-center hover:bg-[#FAF8F5]" data-testid={`increase-${i}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <span className="rounded-full bg-[#D4AF37]/10 px-3 py-1 text-xs font-medium text-[#7A5B12]">Offert x{item.quantity}</span>
                )}
                <span className="font-semibold text-[#2A2A28] w-20 text-right">{(item.price * item.quantity).toFixed(2)} EUR</span>
                <button onClick={() => removeItem(i)} className="text-[#8C3A3A] hover:text-[#7a3232] p-1" data-testid={`remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            {unlockedOffers.length > 0 && (
              <div className="bg-white rounded-xl border border-[#EBE8E0] p-5">
                <h3 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Offres debloquees</h3>
                <div className="space-y-4">
                  {unlockedOffers.map((offer) => (
                    <div key={offer.id} className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-4">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="font-medium text-[#2A2A28]">{offer.title || 'Offre active'}</p>
                          <p className="text-sm text-[#7A7A75]">{offer.description || `${offer.reward_quantity} article(s) offert(s)`}</p>
                        </div>
                        <span className="text-sm font-medium text-[#7A5B12]">
                          Restant a choisir: {offer.remainingRewardCount}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {offer.rewardOptions.map((dish) => (
                          <button
                            key={`${offer.id}-${dish.id}`}
                            type="button"
                            onClick={() => addRewardItem(offer, dish)}
                            disabled={offer.remainingRewardCount <= 0}
                            className="rounded-xl border border-[#EBE8E0] bg-white px-3 py-3 text-left text-sm transition hover:border-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <span className="block font-medium text-[#2A2A28]">{dish.name}</span>
                            <span className="block text-xs text-[#7A7A75]">Ajouter comme article offert</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Customer Info */}
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-5 mt-6" data-testid="customer-info-form">
              <h3 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Vos informations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Prenom *</Label>
                  <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={customer.first_name} onChange={e => setCustomer({...customer, first_name: e.target.value})} data-testid="cart-customer-first-name" />
                </div>
                <div>
                  <Label className="text-sm">Nom *</Label>
                  <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={customer.name} onChange={e => setCustomer({...customer, name: e.target.value})} data-testid="cart-customer-name" />
                </div>
                <div>
                  <Label className="text-sm">Telephone *</Label>
                  <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={customer.phone} onChange={e => setCustomer({...customer, phone: e.target.value})} data-testid="cart-customer-phone" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm">Email</Label>
                  <Input type="email" className="mt-1 rounded-xl border-[#EBE8E0]" value={customer.email} onChange={e => setCustomer({...customer, email: e.target.value})} data-testid="cart-customer-email" />
                </div>
                {orderType === 'delivery' && !tableInfo && (
                  <div className="sm:col-span-2">
                    <Label className="text-sm">Adresse de livraison *</Label>
                    <div className="flex gap-2 mt-1">
                      <Input className="rounded-xl border-[#EBE8E0] flex-1" value={customer.address} onChange={e => { setCustomer({...customer, address: e.target.value}); setDistanceCheck(null); }} data-testid="cart-customer-address" />
                      <Button type="button" variant="outline" className="rounded-xl border-[#EBE8E0] shrink-0" onClick={() => checkDistance(customer.address)} disabled={checkingDistance} data-testid="check-distance-btn">
                        <MapPin className="w-4 h-4 mr-1" /> {checkingDistance ? '...' : 'Verifier'}
                      </Button>
                    </div>
                    {distanceCheck && (
                      <div className={`mt-2 p-2 rounded-lg text-sm flex items-center gap-2 ${distanceCheck.within_range ? 'bg-[#5B6B3F]/10 text-[#5B6B3F]' : 'bg-[#8C3A3A]/10 text-[#8C3A3A]'}`}>
                        {distanceCheck.within_range
                          ? <><MapPin className="w-4 h-4" /> Livraison possible ({distanceCheck.distance_km}km)</>
                          : <><AlertTriangle className="w-4 h-4" /> Hors zone ({distanceCheck.distance_km}km / max {distanceCheck.max_km}km)</>
                        }
                      </div>
                    )}
                    {distanceCheck?.error && <p className="text-xs text-[#8C3A3A] mt-1">{distanceCheck.error}</p>}
                    {settings && <p className="text-xs text-[#7A7A75] mt-1">Zone de livraison : {settings.max_delivery_distance_km}km autour du restaurant</p>}
                  </div>
                )}
                {!tableInfo && (orderType === 'takeaway' || orderType === 'delivery') && (
                  <div className="sm:col-span-2 rounded-xl border border-[#EBE8E0] bg-[#FAF8F5] p-4">
                    <Label className="text-sm font-medium text-[#2A2A28]">
                      {orderType === 'delivery' ? 'Horaire de livraison' : 'Horaire de retrait'}
                    </Label>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setPickupPreference('scheduled')}
                        className={`rounded-xl border px-3 py-3 text-left text-sm ${pickupPreference === 'scheduled' ? 'border-[#5B6B3F] bg-[#5B6B3F]/10 text-[#2A2A28]' : 'border-[#EBE8E0] bg-white text-[#7A7A75]'}`}
                        data-testid="pickup-scheduled"
                      >
                        <span className="block font-medium">Choisir une heure</span>
                        <span className="block text-xs">Je souhaite etre servi vers l'heure indiquee.</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPickupPreference('asap')}
                        className={`rounded-xl border px-3 py-3 text-left text-sm ${pickupPreference === 'asap' ? 'border-[#5B6B3F] bg-[#5B6B3F]/10 text-[#2A2A28]' : 'border-[#EBE8E0] bg-white text-[#7A7A75]'}`}
                        data-testid="pickup-asap"
                      >
                        <span className="block font-medium">Des que possible</span>
                        <span className="block text-xs">Le restaurant confirmera le delai de preparation.</span>
                      </button>
                    </div>
                    {pickupPreference === 'scheduled' ? (
                      <div className="mt-3 grid max-w-lg gap-3 sm:grid-cols-2">
                        <div>
                          <Label className="text-xs flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" /> Date souhaitee</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="mt-1 w-full justify-start rounded-xl border-[#EBE8E0] bg-white text-left text-sm font-normal" data-testid="cart-pickup-date">
                                {selectedPickupDate ? format(selectedPickupDate, 'dd/MM/yyyy') : 'Choisir une date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={selectedPickupDate}
                                onSelect={(date) => {
                                  if (!date) return;
                                  const nextDate = format(date, 'yyyy-MM-dd');
                                  setPickupDate(nextDate);
                                  setPickupTime(availablePickupSlots.find(slot => slot.date === nextDate)?.time || '');
                                }}
                                disabled={(date) => !pickupDates.includes(format(date, 'yyyy-MM-dd'))}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label className="text-xs flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Heure souhaitee</Label>
                          <select
                            className="mt-1 flex h-10 w-full rounded-xl border border-[#EBE8E0] bg-white px-3 text-sm text-[#2A2A28] disabled:bg-[#F5F3EF]"
                            value={pickupTime}
                            onChange={event => setPickupTime(event.target.value)}
                            disabled={!pickupDate || selectedDateSlots.length === 0}
                            data-testid="cart-pickup-time"
                          >
                            {selectedDateSlots.length === 0 ? <option value="">Choisir une date</option> : null}
                            {selectedDateSlots.map(slot => <option key={slot.time} value={slot.time}>{slot.time}</option>)}
                          </select>
                        </div>
                        <p className="sm:col-span-2 text-xs text-[#7A7A75]">Les heures disponibles respectent les horaires et pauses du restaurant.</p>
                      </div>
                    ) : (
                      <p className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-[#5B6B3F]">Le restaurant vous indiquera l'heure prevue et vous avertira des que votre commande est prete.</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[#EBE8E0] p-5" data-testid="payment-method-form">
              <h3 className="font-heading text-lg font-semibold text-[#2A2A28] mb-3">Paiement</h3>
              <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-4 py-3 text-sm text-[#7A5B12]">
                {paymentTimingMessage}
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availablePaymentMethods.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPaymentMethod(key)}
                    className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                      paymentMethod === key
                        ? 'border-[#5B6B3F] bg-[#5B6B3F]/8 text-[#2A2A28]'
                        : 'border-[#EBE8E0] bg-white text-[#7A7A75]'
                    }`}
                    data-testid={`payment-method-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {availablePaymentMethods.length === 0 && (
                <p className="mt-4 text-sm text-[#8C3A3A]">
                  Aucun mode de paiement n'est disponible pour le moment.
                </p>
              )}
              {paymentMethod === 'card' && (
                <p className="mt-4 rounded-xl border border-[#5B6B3F]/20 bg-[#5B6B3F]/8 px-4 py-3 text-sm text-[#5B6B3F]">
                  Paiement securise par SumUp. Des frais de gestion de {cardFeePercent.toFixed(2)}% seront ajoutes au total.
                </p>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-5 sticky top-24" data-testid="order-summary">
              <h3 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Recapitulatif</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#7A7A75]">Sous-total</span><span>{total.toFixed(2)} EUR</span></div>
                {orderType === 'delivery' && deliveryFee > 0 && <div className="flex justify-between"><span className="text-[#7A7A75]">Frais de livraison</span><span>{deliveryFee.toFixed(2)} EUR</span></div>}
                {paymentMethod === 'card' && cardProcessingFee > 0 && <div className="flex justify-between"><span className="text-[#7A7A75]">Frais CB ({cardFeePercent.toFixed(2)}%)</span><span>{cardProcessingFee.toFixed(2)} EUR</span></div>}
                <div className="border-t border-[#EBE8E0] pt-2 mt-2 flex justify-between font-semibold text-base">
                  <span>Total</span><span className="text-[#5B6B3F]">{grandTotal.toFixed(2)} EUR</span>
                </div>
              </div>
              <div className="mt-5 rounded-xl border border-[#EBE8E0] bg-[#FAF8F5] p-4 text-sm text-[#7A7A75]">
                <p className="font-medium text-[#2A2A28] mb-1">Mode de paiement choisi</p>
                <p>{paymentMethodLabels[paymentMethod]}</p>
              </div>
              <Button onClick={handleOrder} disabled={loading || !!settings?.exceptional_closure_enabled || availablePaymentMethods.length === 0} className="w-full mt-4 rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white h-12 font-medium" data-testid="submit-order-btn">
                <Wallet className="w-4 h-4 mr-2" /> {loading ? 'Redirection vers SumUp...' : paymentMethod === 'card' ? 'Payer avec SumUp' : 'Confirmer la commande'}
              </Button>
                <p className="mt-3 text-center text-xs text-[#7A7A75]">
                {paymentTimingMessage}
              </p>
              {tableInfo && (
                <Button onClick={() => handleOrder()} disabled={loading || !!settings?.exceptional_closure_enabled} variant="outline" className="hidden">
                  Confirmer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
